<script lang="ts">
  import HandleSwipe from '$lib/common/HandleSwipe.svelte';
  import type { PictureMetadataTrackLoading } from '../../types';
  import NavArrow from './NavArrow.svelte';

  interface Props {
    picture: PictureMetadataTrackLoading | null;
    onClose: () => void;
    onPrev: () => void;
    onNext: () => void;
  }

  let { picture, onClose, onPrev, onNext }: Props = $props();

  function handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') onClose();
    if (event.key === 'ArrowLeft') onPrev();
    if (event.key === 'ArrowRight') onNext();
  }

  $effect(() => {
    if (picture) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = '';
    };
  });
</script>

<svelte:window onkeydown={handleKeydown} />

{#if picture}
  <HandleSwipe threshold={75} onSwipeLeft={onPrev} onSwipeRight={onNext}>
    <div class="modal-backdrop">
      <button class="close-button" onclick={onClose} aria-label="Close modal"> &times; </button>
      <!-- eslint-disable svelte/no-navigation-without-resolve -->
      <a
        href={picture.default.img.src}
        download={`${picture.alt}.${picture.extension}`}
        class="download-button"
        aria-label="Download photo"
        onclick={(e) => e.stopPropagation()}
      >
        <!-- eslint-enable svelte/no-navigation-without-resolve -->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
        <span class="button-text">Download</span>
      </a>

      <NavArrow
        direction="PREV"
        onclick={onPrev}
        ariaLabel="Previous photo"
        class="lightbox-nav-arrow lightbox-nav-prev"
      />

      <button class="modal-content" aria-label="Photo" onclick={onClose}>
        <enhanced:img src={picture.default} sizes="100vw" class="full-image" alt={picture.alt} />
      </button>

      <NavArrow
        direction="NEXT"
        onclick={onNext}
        ariaLabel="Next photo"
        class="lightbox-nav-arrow lightbox-nav-next"
      />
    </div>
  </HandleSwipe>
{/if}

<style>
  /* 1. Backdrop & Layout */
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.95);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    padding: 0; /* Removed padding to allow image to hit edges if needed */
    box-sizing: border-box;
    overflow: hidden;
  }

  .modal-content {
    position: relative;
    width: 100vw; /* Use full viewport width */
    height: 100vh; /* Use full viewport height */
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    padding: 2rem; /* Buffer so image doesn't touch screen edges */
    cursor: pointer;
    box-sizing: border-box;
  }

  .full-image {
    display: block;
    max-width: 100%;
    max-height: 95vh; /* Allow it to be much taller */
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 4px;
    box-sizing: border-box;
    /* Subtle shadow to lift the image off the black backdrop */
    box-shadow: 0 0 50px rgba(0, 0, 0, 0.5);
  }

  /* 2. Navigation Arrows */
  :global(.lightbox-nav-arrow) {
    position: fixed;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1001;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    color: white;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :global(.lightbox-nav-arrow:hover) {
    background: rgba(255, 255, 255, 0.2);
  }

  :global(.lightbox-nav-prev) {
    left: 1rem;
  }

  :global(.lightbox-nav-next) {
    right: 1rem;
  }

  /* 3. Button Base Styles (Default = Mobile) */
  .download-button,
  .close-button {
    position: fixed;
    top: 15px;
    height: 40px;
    z-index: 1001;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    background: rgba(40, 40, 40, 0.7); /* Slightly darker for better contrast */
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .close-button {
    right: 15px;
    width: 40px;
    font-size: 1.5rem;
    padding: 0;
    line-height: 1;
  }

  .download-button {
    right: 65px;
    width: 40px;
    padding: 0;
    text-decoration: none;
  }

  .download-button svg {
    width: 20px;
    height: 20px;
  }

  .download-button .button-text {
    display: none;
  }

  /* 4. Tablet & Desktop */
  @media (min-width: 768px) {
    .modal-content {
      /* Reclaim horizontal space by reducing padding from 4rem to 1.5rem */
      padding: 1.5rem;
    }

    .full-image {
      /* Increase from 85vh to allow the image to fill the height of the iPad */
      max-height: 92vh;
      /* Force the image to grow to fill the container width if landscape */
      width: 100%;
      max-width: 100%;
      object-fit: contain;
    }

    .download-button,
    .close-button {
      top: 25px;
      height: 44px;
      border-radius: 12px;
    }

    .close-button {
      right: 25px;
      width: 44px;
      font-size: 1.8rem;
    }

    .download-button {
      right: 79px;
      width: auto;
      padding: 0 18px;
      gap: 8px;
    }

    .download-button .button-text {
      display: inline;
      font-size: 0.95rem;
      font-weight: 500;
    }

    /* Keep arrows tucked closer to edges on tablets */
    :global(.lightbox-nav-prev) {
      left: 1.5rem;
    }

    :global(.lightbox-nav-next) {
      right: 1.5rem;
    }
  }

  /* 5. Interaction Styles */
  .download-button:hover,
  .close-button:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.4);
    transform: translateY(-1px);
  }

  .download-button:active,
  .close-button:active {
    transform: scale(0.95);
  }
</style>
